import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Users, TrendingUp, Clock, CheckCircle, AlertCircle, BarChart3, 
  Calendar, RefreshCw, Download, Award, Target, Zap, Settings, 
  Save, RotateCcw, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar, 
  PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate } from '../utils/dateHelpers';
import { toast } from 'sonner';
import './TableroGeneral.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548'];

const TableroRendimiento = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gestiones, setGestiones] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [selectedAsesor, setSelectedAsesor] = useState('');
  const [periodoFiltro, setPeriodoFiltro] = useState('mes');
  const [exportandoPDF, setExportandoPDF] = useState(false);
  const dashboardRef = useRef(null);
  
  // Ponderaciones cargadas del backend
  const [ponderaciones, setPonderaciones] = useState(null);
  const [ponderacionesEditando, setPonderacionesEditando] = useState(null);
  const [mostrarConfigPonderaciones, setMostrarConfigPonderaciones] = useState(false);
  const [guardandoPonderaciones, setGuardandoPonderaciones] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPonderaciones();
  }, []);

  useEffect(() => {
    if (user?.role === 'analyst' && user?.full_name && asesores.length > 0) {
      setSelectedAsesor(user.full_name);
    }
  }, [user, asesores]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [gestionesRes, asesoresRes] = await Promise.all([
        axios.get(`${API}/gestiones?limit=1000`, { headers }),
        axios.get(`${API}/catalogos/asesores-cnc?para_rendimiento=true`, { headers }).catch(() => ({ data: [] }))
      ]);
      setGestiones(gestionesRes.data.gestiones || gestionesRes.data || []);
      setAsesores(asesoresRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPonderaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/ranking/ponderaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPonderaciones(res.data.ponderaciones);
    } catch (error) {
      console.error('Error al cargar ponderaciones:', error);
    }
  };

  const handleGuardarPonderaciones = async () => {
    setGuardandoPonderaciones(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/ranking/ponderaciones`, 
        { ponderaciones: ponderacionesEditando },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPonderaciones(ponderacionesEditando);
      toast.success('Ponderaciones guardadas. El ranking se ha recalculado.');
    } catch (error) {
      toast.error('Error al guardar ponderaciones');
    } finally {
      setGuardandoPonderaciones(false);
    }
  };

  const handleResetPonderaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ranking/ponderaciones/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPonderaciones(res.data.ponderaciones);
      setPonderacionesEditando(res.data.ponderaciones);
      toast.success('Ponderaciones restauradas a valores por defecto');
    } catch (error) {
      toast.error('Error al restaurar ponderaciones');
    }
  };

  // Helper: get weight value
  const getPeso = (key) => {
    if (!ponderaciones || !ponderaciones[key]) return 0;
    const p = ponderaciones[key];
    if (!p.activo) return 0;
    return p.peso || 0;
  };

  const filtrarPorPeriodo = (data) => {
    const hoy = new Date();
    let fechaInicio = new Date();
    switch (periodoFiltro) {
      case 'mes': fechaInicio.setMonth(hoy.getMonth() - 1); break;
      case 'trimestre': fechaInicio.setMonth(hoy.getMonth() - 3); break;
      case 'ano': fechaInicio.setFullYear(hoy.getFullYear() - 1); break;
      default: return data;
    }
    return data.filter(g => new Date(g.createdAt) >= fechaInicio);
  };

  // =============================================================
  // NUEVA FÓRMULA: Métricas por asesor CNC
  // =============================================================
  const calcularMetricasAsesor = (nombreAsesor) => {
    const gestionesFiltradas = filtrarPorPeriodo(gestiones);
    const gestionesAsesor = gestionesFiltradas.filter(g => g.nombre_asesor_cnc === nombreAsesor);
    
    const total = gestionesAsesor.length;
    const completadas = gestionesAsesor.filter(g => g.estado === 'Completada / Archivada').length;
    const enProceso = gestionesAsesor.filter(g => g.estado !== 'Completada / Archivada').length;
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    // Completadas en tiempo vs fuera de plazo (solo CNC)
    let enTiempo = 0;
    let fueraPlazo = 0;
    
    gestionesAsesor.filter(g => g.estado === 'Completada / Archivada').forEach(g => {
      const fechaCompletado = g.updatedAt ? parseLocalDate(g.updatedAt) : new Date();
      const yaDerivada = g.asesor_probidad_id || g.asesor_probidad_nombre;
      let fueraPlazoLocal = false;
      
      if (yaDerivada && g.fecha_asignacion_probidad && g.fecha_vencimiento) {
        const fechaDerivacion = parseLocalDate(g.fecha_asignacion_probidad);
        const fechaVencCNC = parseLocalDate(g.fecha_vencimiento);
        fueraPlazoLocal = fechaDerivacion && fechaVencCNC && fechaDerivacion > fechaVencCNC;
      } else if (g.fecha_vencimiento) {
        const fechaVencCNC = parseLocalDate(g.fecha_vencimiento);
        fueraPlazoLocal = fechaCompletado && fechaVencCNC && fechaCompletado > fechaVencCNC;
      }
      
      if (fueraPlazoLocal) fueraPlazo++;
      else enTiempo++;
    });
    
    // Vencidas CNC (no derivadas, no completadas)
    const vencidasCNC = gestionesAsesor.filter(g => {
      if (g.estado === 'Completada / Archivada') return false;
      const yaDerivada = g.asesor_probidad_id || g.asesor_probidad_nombre;
      if (yaDerivada) return false;
      const fechaVenc = g.fecha_vencimiento ? parseLocalDate(g.fecha_vencimiento) : null;
      return fechaVenc && hoy > fechaVenc;
    }).length;
    
    // Urgentes
    const urgentes = gestionesAsesor.filter(g => g.es_urgente).length;
    const urgentesCompletados = gestionesAsesor.filter(g => g.es_urgente && g.estado === 'Completada / Archivada').length;
    
    // Calificaciones
    const conCalificacion = gestionesAsesor.filter(g => g.calificacion_instancia?.puntuacion);
    const totalCalificaciones = conCalificacion.length;
    
    // Total derivadas a Probidad
    const derivadasTotal = gestionesAsesor.filter(g => g.asesor_probidad_id || g.asesor_probidad_nombre).length;
    
    // Derivadas a Probidad en tiempo (antes de vencer CNC)
    const derivadasEnTiempo = gestionesAsesor.filter(g => {
      const yaDerivada = g.asesor_probidad_id || g.asesor_probidad_nombre;
      if (!yaDerivada) return false;
      if (!g.fecha_vencimiento || !g.fecha_asignacion_probidad) return !!yaDerivada;
      const fechaDerivacion = parseLocalDate(g.fecha_asignacion_probidad);
      const fechaVencCNC = parseLocalDate(g.fecha_vencimiento);
      return fechaDerivacion && fechaVencCNC && fechaDerivacion <= fechaVencCNC;
    }).length;
    
    // Vencidas Probidad (SOLO INFORMATIVO, no penaliza)
    const vencidasProbidad = gestionesAsesor.filter(g => {
      if (g.estado === 'Completada / Archivada') return false;
      if (!g.fecha_vencimiento_probidad) return false;
      const fechaVenc = parseLocalDate(g.fecha_vencimiento_probidad);
      return fechaVenc && hoy > fechaVenc;
    }).length;
    
    // ===== NUEVA FÓRMULA DE % CUMPLIMIENTO CNC =====
    // %CumplimientoCNC = (Derivadas en tiempo / Total asignadas) * 100
    const cumplimientoCNC = total > 0 ? ((derivadasEnTiempo / total) * 100).toFixed(1) : 0;
    
    // ===== NUEVO INDICADOR: % GESTIONES COMPLETADAS POR PROBIDAD =====
    // %GestionesCompletadas = (Completadas que fueron derivadas / Total derivadas) * 100
    const derivadasCompletadas = gestionesAsesor.filter(g => {
      const yaDerivada = g.asesor_probidad_id || g.asesor_probidad_nombre;
      return yaDerivada && g.estado === 'Completada / Archivada';
    }).length;
    const pctGestionesCompletadas = derivadasTotal > 0 ? ((derivadasCompletadas / derivadasTotal) * 100).toFixed(1) : 0;
    
    // Por tipo y estado
    const porTipo = {};
    gestionesAsesor.forEach(g => { porTipo[g.tipo_gestion] = (porTipo[g.tipo_gestion] || 0) + 1; });
    const porEstado = {};
    gestionesAsesor.forEach(g => { porEstado[g.estado] = (porEstado[g.estado] || 0) + 1; });
    
    return {
      total, completadas, enProceso, enTiempo, fueraPlazo,
      vencidasCNC, vencidasProbidad,
      derivadasTotal, derivadasEnTiempo, derivadasCompletadas,
      urgentes, urgentesCompletados,
      totalCalificaciones,
      cumplimientoCNC,
      pctGestionesCompletadas,
      porTipo, porEstado
    };
  };

  // =============================================================
  // NUEVO RANKING: Separando Positivos y Penalizaciones
  // =============================================================
  const calcularRankingAsesores = () => {
    if (!ponderaciones) return [];
    
    const asesoresAnalistas = asesores
      .filter(a => a.activo !== false)
      .map(a => a.nombre_completo);
    const asesoresValidos = [...new Set(asesoresAnalistas)];
    
    return asesoresValidos.map(nombre => {
      const m = calcularMetricasAsesor(nombre);
      
      // ===== PUNTAJE POSITIVO =====
      const puntajePositivo = 
        (m.completadas * getPeso('completadas')) +
        (m.enProceso * getPeso('enProceso')) +
        (parseFloat(m.cumplimientoCNC) * getPeso('cumplimientoCNC')) +
        (parseFloat(m.pctGestionesCompletadas) * getPeso('gestionesCompletadas')) +
        (m.totalCalificaciones * getPeso('calificaciones')) +
        (m.urgentesCompletados * getPeso('urgentes')) +
        (m.derivadasEnTiempo * getPeso('derivadasTiempo')) +
        (m.derivadasTotal * getPeso('derivadasTotal'));
      
      // ===== PENALIZACIÓN (solo CNC, NUNCA Probidad) =====
      const penalizacion = 
        (m.vencidasCNC * getPeso('vencidasCNC')) +
        (m.fueraPlazo * getPeso('fueraPlazo'));
      
      const scoreFinal = Math.round((puntajePositivo - penalizacion) * 10) / 10;
      
      return {
        nombre, ...m,
        puntajePositivo: Math.round(puntajePositivo * 10) / 10,
        penalizacion: Math.round(penalizacion * 10) / 10,
        score: scoreFinal
      };
    }).sort((a, b) => b.score - a.score);
  };

  const getTendenciaMensual = (nombreAsesor) => {
    const gestionesAsesor = gestiones.filter(g => g.nombre_asesor_cnc === nombreAsesor);
    const mesesData = {};
    const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    gestionesAsesor.forEach(g => {
      if (g.createdAt) {
        const fecha = new Date(g.createdAt);
        const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        if (!mesesData[key]) mesesData[key] = { asignadas: 0, completadas: 0 };
        mesesData[key].asignadas++;
        if (g.estado === 'Completada / Archivada') mesesData[key].completadas++;
      }
    });
    return Object.entries(mesesData).sort(([a], [b]) => a.localeCompare(b)).slice(-6).map(([key, value]) => {
      const [year, month] = key.split('-');
      return { mes: `${nombresMeses[parseInt(month) - 1]} ${year.slice(2)}`, ...value };
    });
  };

  const handleExportarPDF = async () => {
    if (!dashboardRef.current) return;
    setExportandoPDF(true);
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#f5f7fa' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const imgWidth = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`Tablero_Rendimiento_${selectedAsesor || 'General'}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
    } finally {
      setExportandoPDF(false);
    }
  };

  if (loading || !ponderaciones) {
    return (
      <div className="tablero-loading">
        <div className="loader"></div>
        <p>Cargando datos de rendimiento...</p>
      </div>
    );
  }

  const ranking = calcularRankingAsesores();
  const metricasSeleccionado = selectedAsesor ? calcularMetricasAsesor(selectedAsesor) : null;
  const tendencia = selectedAsesor ? getTendenciaMensual(selectedAsesor) : [];

  const getRadarData = () => {
    if (!metricasSeleccionado) return [];
    const maxCompletadas = Math.max(...ranking.map(r => r.completadas)) || 1;
    const maxTotal = Math.max(...ranking.map(r => r.total)) || 1;
    const maxCalificaciones = Math.max(...ranking.map(r => r.totalCalificaciones)) || 1;
    return [
      { subject: 'Productividad', A: (metricasSeleccionado.completadas / maxCompletadas) * 100, fullMark: 100 },
      { subject: '% Cumpl. CNC', A: parseFloat(metricasSeleccionado.cumplimientoCNC), fullMark: 100 },
      { subject: 'Calificaciones', A: (metricasSeleccionado.totalCalificaciones / maxCalificaciones) * 100, fullMark: 100 },
      { subject: 'Volumen', A: (metricasSeleccionado.total / maxTotal) * 100, fullMark: 100 },
      { subject: 'Sin Vencidos', A: metricasSeleccionado.total > 0 ? ((1 - metricasSeleccionado.vencidasCNC / metricasSeleccionado.total) * 100) : 100, fullMark: 100 },
    ];
  };

  // Helper for table header style
  const thStyle = (extra = {}) => ({ 
    minWidth: '55px', 
    background: 'var(--table-header-bg, linear-gradient(135deg, #667eea 0%, #764ba2 100%))', 
    color: 'white', position: 'sticky', top: 0, padding: '0.5rem 0.3rem', 
    textAlign: 'center', whiteSpace: 'normal', lineHeight: 1.2, fontSize: '0.75rem',
    ...extra 
  });

  return (
    <div className="tablero-container" ref={dashboardRef}>
      <div className="tablero-header">
        <div>
          <h1><Users size={28} /> Ranking de Colaboradores</h1>
          <p>Métricas de productividad y desempeño por asesor CNC</p>
        </div>
        <div className="tablero-actions">
          <select value={periodoFiltro} onChange={(e) => setPeriodoFiltro(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '6px', marginRight: '0.5rem' }}>
            <option value="mes">Último mes</option>
            <option value="trimestre">Último trimestre</option>
            <option value="ano">Último año</option>
            <option value="todo">Todo el histórico</option>
          </select>
          <button className="btn btn-secondary" onClick={() => { fetchData(); fetchPonderaciones(); }} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
          </button>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => { setPonderacionesEditando(JSON.parse(JSON.stringify(ponderaciones))); setMostrarConfigPonderaciones(!mostrarConfigPonderaciones); }}
              data-testid="btn-config-ponderaciones"
              style={{ background: mostrarConfigPonderaciones ? '#1565C0' : undefined, color: mostrarConfigPonderaciones ? 'white' : undefined }}>
              <Settings size={16} /> Parametrización
            </button>
          )}
          <button className="btn btn-primary" onClick={handleExportarPDF} disabled={exportandoPDF}>
            <Download size={16} /> {exportandoPDF ? 'Exportando...' : 'Exportar PDF'}
          </button>
        </div>
      </div>

      {/* ===== PANEL DE PARAMETRIZACIÓN ADMINISTRATIVA ===== */}
      {mostrarConfigPonderaciones && ponderacionesEditando && (
        <div className="panel" style={{ marginBottom: '1.5rem', border: '2px solid #1565C0' }} data-testid="panel-parametrizacion">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1565C0' }}>
              <Settings size={22} /> Parametrización de Ponderaciones
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={handleResetPonderaciones} data-testid="btn-reset-ponderaciones">
                <RotateCcw size={14} /> Restaurar Defaults
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleGuardarPonderaciones} disabled={guardandoPonderaciones} data-testid="btn-guardar-ponderaciones">
                <Save size={14} /> {guardandoPonderaciones ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
          
          <div style={{ 
            background: '#E8EAF6', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem',
            borderLeft: '3px solid #3F51B5', fontSize: '0.8rem', color: '#283593'
          }}>
            <Shield size={14} style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} />
            <strong>Regla:</strong> El analista CNC no es penalizado por atrasos o vencimientos de Probidad. "Venc. Prob." es solo informativo.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Positivos */}
            <div>
              <h4 style={{ color: '#2E7D32', margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <TrendingUp size={16} /> Componentes Positivos
              </h4>
              {Object.entries(ponderacionesEditando).filter(([, v]) => v.tipo === 'positivo').map(([key, val]) => (
                <div key={key} style={{ 
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', 
                  background: val.activo ? '#F1F8E9' : '#F5F5F5', borderRadius: '6px', marginBottom: '0.4rem',
                  opacity: val.activo ? 1 : 0.6
                }}>
                  <input type="checkbox" checked={val.activo}
                    onChange={(e) => setPonderacionesEditando(prev => ({...prev, [key]: {...prev[key], activo: e.target.checked}}))}
                    data-testid={`check-${key}`}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{key}</div>
                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{val.descripcion}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>Peso:</span>
                    <input type="number" step="0.5" min="0" max="50" value={val.peso}
                      onChange={(e) => setPonderacionesEditando(prev => ({...prev, [key]: {...prev[key], peso: parseFloat(e.target.value) || 0}}))}
                      style={{ width: '60px', padding: '0.25rem', borderRadius: '4px', border: '1px solid #C8E6C9', textAlign: 'center' }}
                      disabled={!val.activo}
                      data-testid={`peso-${key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Penalizaciones */}
            <div>
              <h4 style={{ color: '#C62828', margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <AlertCircle size={16} /> Penalizaciones (solo CNC)
              </h4>
              {Object.entries(ponderacionesEditando).filter(([, v]) => v.tipo === 'penalizacion').map(([key, val]) => (
                <div key={key} style={{ 
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', 
                  background: val.activo ? '#FFEBEE' : '#F5F5F5', borderRadius: '6px', marginBottom: '0.4rem',
                  opacity: val.activo ? 1 : 0.6
                }}>
                  <input type="checkbox" checked={val.activo}
                    onChange={(e) => setPonderacionesEditando(prev => ({...prev, [key]: {...prev[key], activo: e.target.checked}}))}
                    data-testid={`check-${key}`}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{key}</div>
                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{val.descripcion}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>Peso:</span>
                    <input type="number" step="0.5" min="0" max="50" value={val.peso}
                      onChange={(e) => setPonderacionesEditando(prev => ({...prev, [key]: {...prev[key], peso: parseFloat(e.target.value) || 0}}))}
                      style={{ width: '60px', padding: '0.25rem', borderRadius: '4px', border: '1px solid #FFCDD2', textAlign: 'center' }}
                      disabled={!val.activo}
                      data-testid={`peso-${key}`}
                    />
                  </div>
                </div>
              ))}
              <div style={{ 
                background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: '6px', 
                padding: '0.5rem 0.75rem', marginTop: '0.75rem', fontSize: '0.75rem', color: '#E65100' 
              }}>
                <strong>Nota:</strong> "Venc. Prob." se muestra solo como referencia informativa y NO resta puntos al analista CNC.
              </div>
            </div>
          </div>
          
          {/* Preview de la fórmula */}
          <div style={{ marginTop: '1rem', background: 'white', border: '1px solid #90CAF9', borderRadius: '8px', padding: '0.75rem' }}>
            <h4 style={{ margin: '0 0 0.5rem', color: '#1565C0', fontSize: '0.85rem' }}>Fórmula Resultante:</h4>
            <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', background: '#F5F5F5', padding: '0.5rem', borderRadius: '4px' }}>
              <div style={{ color: '#2E7D32', marginBottom: '0.25rem' }}>
                <strong>Puntaje +</strong> = {Object.entries(ponderacionesEditando).filter(([,v]) => v.tipo === 'positivo' && v.activo).map(([k,v]) => `(${k}×${v.peso})`).join(' + ') || '0'}
              </div>
              <div style={{ color: '#C62828', marginBottom: '0.25rem' }}>
                <strong>Penalización -</strong> = {Object.entries(ponderacionesEditando).filter(([,v]) => v.tipo === 'penalizacion' && v.activo).map(([k,v]) => `(${k}×${v.peso})`).join(' + ') || '0'}
              </div>
              <div style={{ color: '#1565C0', fontWeight: 700 }}>
                <strong>Score Final</strong> = Puntaje Positivo - Penalización
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selector de Asesor */}
      <div className="panel" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={18} /> Seleccionar Colaborador:
          </label>
          <select value={selectedAsesor} onChange={(e) => setSelectedAsesor(e.target.value)}
            disabled={user?.role === 'analyst'}
            style={{ padding: '0.75rem 1rem', borderRadius: '8px', minWidth: '300px', border: '2px solid var(--color-primary)', fontSize: '1rem',
              ...(user?.role === 'analyst' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}) }}>
            <option value="">-- Seleccione un asesor --</option>
            {asesores.map(asesor => (
              <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
            ))}
          </select>
          {user?.role === 'analyst' && (
            <span style={{ fontSize: '0.85rem', color: '#666' }}>(Solo puede ver sus propias métricas)</span>
          )}
        </div>
      </div>

      {/* ===== RANKING TABLE ===== */}
      {user?.role !== 'analyst' && (
        <div className="panel" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Award size={20} /> Ranking de Colaboradores
          </h2>
          
          {/* Fórmula resumida */}
          <div style={{ 
            background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', borderRadius: '8px', 
            padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #90CAF9', fontSize: '0.8rem'
          }}>
            <div style={{ fontFamily: 'monospace', marginBottom: '0.25rem' }}>
              <span style={{ color: '#2E7D32' }}><strong>Score</strong> = Puntaje Positivo</span> - <span style={{ color: '#C62828' }}>Penalización</span>
            </div>
            <div style={{ fontSize: '0.7rem', color: '#1565C0' }}>
              <strong>% Cumpl. CNC</strong> = Derivadas en tiempo / Total asignadas | <strong>% Gest. Compl.</strong> = Finalizadas por Probidad / Total derivadas (bono, no castiga)
            </div>
          </div>
          
          <div style={{ overflowX: 'auto', maxHeight: '500px', position: 'relative' }}>
            <table className="data-table" style={{ minWidth: '1600px', borderCollapse: 'separate', borderSpacing: 0 }} data-testid="ranking-table">
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={thStyle({ minWidth: '35px' })}>#</th>
                  <th style={thStyle({ minWidth: '140px', textAlign: 'left' })}>Colaborador</th>
                  <th style={thStyle()}>Total</th>
                  <th style={thStyle()}>Compl.</th>
                  <th style={thStyle()}>En Proc.</th>
                  <th style={thStyle()}>Deriv. Total</th>
                  <th style={thStyle()}>Deriv. Tiempo</th>
                  <th style={thStyle()}>Calif.</th>
                  <th style={thStyle()}>Urg. Atend.</th>
                  <th style={thStyle()}>Venc. CNC</th>
                  <th style={thStyle({ background: '#78909C', minWidth: '55px' })} title="Solo informativo, NO penaliza">Venc. Prob. (info)</th>
                  <th style={thStyle({ background: '#1565C0', minWidth: '65px' })} title="Derivadas en tiempo / Total asignadas">% Cumpl. CNC</th>
                  <th style={thStyle({ background: '#00897B', minWidth: '65px' })} title="Completadas por Probidad / Total derivadas (bono)">% Gest. Compl.</th>
                  <th style={thStyle({ background: '#2E7D32', minWidth: '65px' })}>Puntaje +</th>
                  <th style={thStyle({ background: '#C62828', minWidth: '65px' })}>Penaliz.</th>
                  <th style={thStyle({ minWidth: '70px', fontWeight: 700 })}>Score Final</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((a, index) => (
                  <tr key={a.nombre} onClick={() => setSelectedAsesor(a.nombre)}
                    style={{ cursor: 'pointer', backgroundColor: selectedAsesor === a.nombre ? 'var(--color-primary-light, #E3F2FD)' : 'inherit' }}>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem' }}>
                      {index === 0 && <span style={{ fontSize: '1rem' }}>🥇</span>}
                      {index === 1 && <span style={{ fontSize: '1rem' }}>🥈</span>}
                      {index === 2 && <span style={{ fontSize: '1rem' }}>🥉</span>}
                      {index > 2 && <span style={{ fontWeight: 500 }}>{index + 1}</span>}
                    </td>
                    <td style={{ fontWeight: 600, padding: '0.5rem 0.4rem', fontSize: '0.85rem' }}>{a.nombre}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem' }}>{a.total}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#4CAF50', fontWeight: 600 }}>{a.completadas}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#2196F3', fontWeight: 600 }}>{a.enProceso}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#1565C0', fontWeight: 600 }}>{a.derivadasTotal}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#2196F3', fontWeight: 600 }}>{a.derivadasEnTiempo}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#FFB800', fontWeight: 600 }}>{a.totalCalificaciones}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#FF5722', fontWeight: 600 }}>{a.urgentesCompletados}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: a.vencidasCNC > 0 ? '#D32F2F' : '#4CAF50', fontWeight: 600 }}>{a.vencidasCNC}</td>
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#78909C', fontStyle: 'italic' }}>{a.vencidasProbidad}</td>
                    {/* % Cumplimiento CNC */}
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem' }}>
                      <span style={{ 
                        background: parseFloat(a.cumplimientoCNC) >= 80 ? '#E8F5E9' : parseFloat(a.cumplimientoCNC) >= 50 ? '#FFF3E0' : '#FFEBEE',
                        padding: '0.15rem 0.35rem', borderRadius: '4px', fontWeight: 600, fontSize: '0.8rem',
                        color: parseFloat(a.cumplimientoCNC) >= 80 ? '#2E7D32' : parseFloat(a.cumplimientoCNC) >= 50 ? '#E65100' : '#C62828'
                      }}>{a.cumplimientoCNC}%</span>
                    </td>
                    {/* % Gestiones Completadas */}
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem' }}>
                      <span style={{ 
                        background: parseFloat(a.pctGestionesCompletadas) >= 80 ? '#E0F2F1' : parseFloat(a.pctGestionesCompletadas) >= 50 ? '#FFF8E1' : '#FBE9E7',
                        padding: '0.15rem 0.35rem', borderRadius: '4px', fontWeight: 600, fontSize: '0.8rem',
                        color: parseFloat(a.pctGestionesCompletadas) >= 80 ? '#00695C' : parseFloat(a.pctGestionesCompletadas) >= 50 ? '#F57F17' : '#BF360C'
                      }}>{a.pctGestionesCompletadas}%</span>
                    </td>
                    {/* Puntaje Positivo */}
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: '#2E7D32', fontWeight: 600, fontSize: '0.85rem' }}>+{a.puntajePositivo}</td>
                    {/* Penalización */}
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', color: a.penalizacion > 0 ? '#C62828' : '#4CAF50', fontWeight: 600, fontSize: '0.85rem' }}>
                      {a.penalizacion > 0 ? `-${a.penalizacion}` : '0'}
                    </td>
                    {/* Score Final */}
                    <td style={{ textAlign: 'center', padding: '0.5rem 0.3rem', fontWeight: 700, fontSize: '0.95rem' }}>
                      <span 
                        title={`═══ DESGLOSE ═══\n\n✅ POSITIVO: +${a.puntajePositivo}\n  Compl.(${a.completadas}×${getPeso('completadas')}) EnProc.(${a.enProceso}×${getPeso('enProceso')}) %CNC(${a.cumplimientoCNC}×${getPeso('cumplimientoCNC')}) %Comp(${a.pctGestionesCompletadas}×${getPeso('gestionesCompletadas')}) Calif(${a.totalCalificaciones}×${getPeso('calificaciones')}) Urg(${a.urgentesCompletados}×${getPeso('urgentes')}) DerivT(${a.derivadasEnTiempo}×${getPeso('derivadasTiempo')}) DerivTot(${a.derivadasTotal}×${getPeso('derivadasTotal')})\n\n❌ PENALIZACIÓN: -${a.penalizacion}\n  VencCNC(${a.vencidasCNC}×${getPeso('vencidasCNC')}) FueraPlazo(${a.fueraPlazo}×${getPeso('fueraPlazo')})\n\n══════════\nSCORE: ${a.score}`}
                        style={{ 
                          background: a.score > 50 ? '#E8F5E9' : a.score > 0 ? '#FFF3E0' : '#FFEBEE',
                          padding: '0.25rem 0.5rem', borderRadius: '6px', cursor: 'help',
                          color: a.score > 50 ? '#1B5E20' : a.score > 0 ? '#E65100' : '#B71C1C'
                        }}
                        data-testid={`score-${index}`}
                      >{a.score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== DETALLE DEL ASESOR ===== */}
      {selectedAsesor && metricasSeleccionado && (
        <>
          <h2 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Target size={20} /> Métricas de: {selectedAsesor}
          </h2>
          
          <div className="kpi-grid">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon"><BarChart3 size={32} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{metricasSeleccionado.total}</div>
                <div className="kpi-label">Total Asignadas</div>
              </div>
            </div>
            <div className="kpi-card kpi-success">
              <div className="kpi-icon"><CheckCircle size={32} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{metricasSeleccionado.completadas}</div>
                <div className="kpi-label">Completadas</div>
              </div>
            </div>
            <div className="kpi-card" style={{ borderTop: '4px solid #1565C0' }}>
              <div className="kpi-icon"><TrendingUp size={32} style={{ color: '#1565C0' }} /></div>
              <div className="kpi-content">
                <div className="kpi-value" style={{ color: '#1565C0' }}>{metricasSeleccionado.cumplimientoCNC}%</div>
                <div className="kpi-label">% Cumpl. CNC</div>
                <div style={{ fontSize: '0.65rem', color: '#666' }}>Derivadas en tiempo / Total</div>
              </div>
            </div>
            <div className="kpi-card" style={{ borderTop: '4px solid #00897B' }}>
              <div className="kpi-icon"><Award size={32} style={{ color: '#00897B' }} /></div>
              <div className="kpi-content">
                <div className="kpi-value" style={{ color: '#00897B' }}>{metricasSeleccionado.pctGestionesCompletadas}%</div>
                <div className="kpi-label">% Gest. Completadas</div>
                <div style={{ fontSize: '0.65rem', color: '#666' }}>Bono (no castiga)</div>
              </div>
            </div>
            <div className="kpi-card" style={{ borderTop: '4px solid #00C853' }}>
              <div className="kpi-icon"><Zap size={32} style={{ color: '#00C853' }} /></div>
              <div className="kpi-content">
                <div className="kpi-value" style={{ color: '#00C853' }}>{metricasSeleccionado.derivadasEnTiempo}</div>
                <div className="kpi-label">Derivadas en Tiempo</div>
              </div>
            </div>
            <div className="kpi-card kpi-danger">
              <div className="kpi-icon"><AlertCircle size={32} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{metricasSeleccionado.vencidasCNC}</div>
                <div className="kpi-label">Vencidas CNC</div>
              </div>
            </div>
            <div className="kpi-card" style={{ borderTop: '4px solid #FFB800' }}>
              <div className="kpi-icon"><span style={{ fontSize: '2rem' }}>★</span></div>
              <div className="kpi-content">
                <div className="kpi-value" style={{ color: '#FFB800' }}>{metricasSeleccionado.totalCalificaciones}</div>
                <div className="kpi-label">Calificaciones</div>
              </div>
            </div>
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon"><AlertCircle size={32} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{metricasSeleccionado.urgentesCompletados}</div>
                <div className="kpi-label">Urgentes Atendidos</div>
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="charts-grid" style={{ marginTop: '1.5rem' }}>
            <div className="chart-card">
              <h3>Tendencia Mensual</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={tendencia}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="asignadas" name="Asignadas" fill="#2196F3" />
                  <Bar dataKey="completadas" name="Completadas" fill="#4CAF50" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-card">
              <h3>Radar de Desempeño</h3>
              <ResponsiveContainer width="100%" height={250}>
                <RadarChart data={getRadarData()}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar name={selectedAsesor} dataKey="A" stroke="#2196F3" fill="#2196F3" fillOpacity={0.6} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-card">
              <h3>Distribución por Módulo</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={Object.entries(metricasSeleccionado.porTipo).map(([name, value]) => ({ name, value }))}
                    cx="50%" cy="50%" outerRadius={80} dataKey="value"
                    label={({ name, percent }) => `${name.substring(0,10)}.. ${(percent * 100).toFixed(0)}%`}>
                    {Object.entries(metricasSeleccionado.porTipo).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-card">
              <h3>Distribución por Estado</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={Object.entries(metricasSeleccionado.porEstado).map(([name, value]) => ({ name: name.substring(0, 15), value, fullName: name }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value, name, props) => [value, props.payload.fullName]} />
                  <Bar dataKey="value" name="Gestiones">
                    {Object.entries(metricasSeleccionado.porEstado).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {!selectedAsesor && (
        <div className="panel" style={{ textAlign: 'center', padding: '3rem' }}>
          <Users size={64} style={{ color: '#ccc', marginBottom: '1rem' }} />
          <h3 style={{ color: '#666' }}>Seleccione un colaborador</h3>
          <p style={{ color: '#999' }}>Elija un asesor CNC del selector superior para ver sus métricas de rendimiento</p>
        </div>
      )}
    </div>
  );
};

export default TableroRendimiento;
